import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import type { TargetFilters } from '../hooks/useTargetSearch';
import { normaliseCountry } from '../profile/ProfileMarketsCard';
import { FOLLOWER_PRESETS, FollowerPreset } from './findKinds';
import { findMessages as messages } from './findMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Find.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FindFiltersProps {
  filters: TargetFilters;
  onChange: (patch: Partial<TargetFilters>) => void;
  shown: number;
  total: number;
}

function presetOf(filters: TargetFilters): FollowerPreset {
  const match = (Object.keys(FOLLOWER_PRESETS) as FollowerPreset[]).find(
    (key) => FOLLOWER_PRESETS[key].min === filters.minFollowers && FOLLOWER_PRESETS[key].max === filters.maxFollowers
  );
  return match ?? 'any';
}

/**
 * The filter bar of Find (design outreach c1): has email, size, country, the name search and the
 * bought and inactive rows (hidden by default; shown with their red chip).
 */
const FindFiltersComponent: React.FC<FindFiltersProps> = ({ filters, onChange, shown, total }) => {
  const intl = useIntl();
  const [q, setQ] = useState(filters.q);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (q !== filters.q) {
        onChange({ q });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.q, onChange, q]);

  return (
    <div className={styles.filters}>
      <input
        className={clsx(shared.input, styles.narrow)}
        value={q}
        placeholder={intl.formatMessage(messages.filterSearch)}
        aria-label={intl.formatMessage(messages.filterSearch)}
        onChange={(event) => setQ(event.target.value)}
      />
      <button
        type="button"
        className={clsx(shared.chip, shared.chipButton, filters.onlyWithEmail && shared.chipOn)}
        aria-pressed={filters.onlyWithEmail}
        onClick={() => onChange({ onlyWithEmail: !filters.onlyWithEmail })}
      >
        {intl.formatMessage(messages.filterEmail)}
      </button>
      <select
        className="form-select form-select-sm w-auto"
        aria-label={intl.formatMessage(messages.filterFollowers)}
        value={presetOf(filters)}
        onChange={(event) => {
          const preset = FOLLOWER_PRESETS[event.target.value as FollowerPreset];
          onChange({ minFollowers: preset.min, maxFollowers: preset.max });
        }}
      >
        <option value="any">{intl.formatMessage(messages.followersAny)}</option>
        <option value="small">{intl.formatMessage(messages.followersSmall)}</option>
        <option value="mid">{intl.formatMessage(messages.followersMid)}</option>
        <option value="big">{intl.formatMessage(messages.followersBig)}</option>
      </select>
      <input
        className={clsx(shared.input, styles.country)}
        defaultValue={filters.country ?? ''}
        placeholder={intl.formatMessage(messages.filterCountry)}
        aria-label={intl.formatMessage(messages.filterCountry)}
        onBlur={(event) => onChange({ country: normaliseCountry(event.target.value) })}
      />
      <button
        type="button"
        className={clsx(shared.chip, shared.chipButton, filters.includeFlagged && shared.chipOn)}
        aria-pressed={filters.includeFlagged}
        onClick={() => onChange({ includeFlagged: !filters.includeFlagged })}
      >
        {intl.formatMessage(messages.filterFlagged)}
      </button>
      <span className={shared.grow} />
      <span className={shared.small}>{intl.formatMessage(messages.resultCount, { shown, total })}</span>
    </div>
  );
};

export const FindFilters = TypedMemo(FindFiltersComponent);
